
import React, { createContext, useContext, useState, useCallback } from "react";
import { ComponentOption, PcConfiguration } from "@/types/types";
import { mockComponents } from "@/data/mockPcComponents";
import { toast } from "sonner";
import { PCConfigContextType } from "./types";
import { useAuth } from "@/hooks/useAuth";
import { saveConfigurationToSupabase } from "./utils/saveConfiguration";
import { saveConfigurationToLocalStorage } from "./utils/localStorage";
import { getInitialConfiguration } from "./utils/configUtils";
import { resolveIncompatibilities } from "@/utils/configuratorConflictResolution";

const PCConfigContext = createContext<PCConfigContextType | undefined>(undefined);

export const PCConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth();
  const [configuration, setConfiguration] = useState<PcConfiguration>(getInitialConfiguration());

  const handleSelectComponent = useCallback((component: ComponentOption, selectedCategory: string) => {
    setConfiguration(prevConfiguration => {
      const newConfiguration = { ...prevConfiguration, [selectedCategory]: component };
      const { resolvedConfiguration, notifications } = resolveIncompatibilities(
        newConfiguration,
        selectedCategory
      );
      const finalConfiguration = { ...newConfiguration, ...resolvedConfiguration };

      toast("Component Selected", {
        description: `${component.name} added to your build.`,
      });

      notifications.forEach(notification => {
        toast(notification.title, {
          description: notification.description,
        });
      });

      return finalConfiguration;
    });
  }, []);

  const handleFanSelection = (fanComponent: ComponentOption, quantity: number) => {
    if (quantity > 0) {
      setConfiguration(prev => ({
        ...prev,
        fans: { component: fanComponent, quantity }
      }));
      
      toast("Fans Updated", {
        description: `${quantity}x ${fanComponent.name} in your build.`,
      });
    } else {
      handleRemoveComponent('fans');
    }
  };

  const handleToggleAccessory = (accessory: ComponentOption) => {
    setConfiguration(prev => {
      const currentAccessories = prev.accessories || [];
      const existingIndex = currentAccessories.findIndex(a => a.id === accessory.id);
      let newAccessories;

      if (existingIndex > -1) {
        newAccessories = currentAccessories.filter(a => a.id !== accessory.id);
        toast("Accessory Removed", {
          description: `${accessory.name} removed from your build.`,
        });
      } else {
        newAccessories = [...currentAccessories, accessory];
        toast("Accessory Added", {
          description: `${accessory.name} added to your build.`,
        });
      }

      return { ...prev, accessories: newAccessories.length > 0 ? newAccessories : null };
    });
  };

  const handleRemoveComponent = (categoryId: string) => {
    const categoryName = mockComponents.find(c => c.id === categoryId)?.name || "Component";
    
    setConfiguration(prev => ({
      ...prev,
      [categoryId]: null
    }));
    
    toast(`${categoryName} Removed`, {
      description: `${categoryName} has been removed from your build.`,
    });
  };

  const validateConfiguration = () => {
    const missingRequiredComponents = mockComponents
      .filter(category => category.required && !configuration[category.id as keyof PcConfiguration])
      .map(category => category.name);

    if (missingRequiredComponents.length > 0) {
      toast("Missing Required Components", {
        description: `Please select the following components: ${missingRequiredComponents.join(", ")}`,
      });
      return false;
    }
    return true;
  };

  const loadConfiguration = (config: PcConfiguration) => {
    setConfiguration(config);
  };

  const handleSaveConfiguration = useCallback(async () => {
    const timestamp = new Date().toISOString();
    const configName = `PC Build ${new Date().toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit'
    })}`;

    const configToSave = {
      configuration,
      savedAt: timestamp,
      name: configName
    };
    
    if (currentUser) {
      try {
        const success = await saveConfigurationToSupabase(
          currentUser.id, 
          configName, 
          configuration
        );
        
        if (success) {
          toast("Configuration Saved", {
            description: "Your PC build has been saved to your account.",
          });
          return true;
        } else {
          throw new Error("Failed to save configuration");
        }
      } catch (error: any) {
        console.error("Error saving configuration to database:", error);
        toast("Error Saving Configuration", {
          description: "There was a problem saving your configuration. Please try again.",
        });
        saveConfigurationToLocalStorage(configToSave);
        return false;
      }
    } else {
      saveConfigurationToLocalStorage(configToSave);
      toast("Configuration Saved Locally", {
        description: "Sign in to save configurations to your account.",
      });
      return true;
    }
  }, [configuration, currentUser]);

  const value = {
    configuration,
    handleSelectComponent,
    handleRemoveComponent,
    validateConfiguration,
    handleSaveConfiguration,
    handleFanSelection,
    handleToggleAccessory,
    loadConfiguration
  };

  return (
    <PCConfigContext.Provider value={value}>
      {children}
    </PCConfigContext.Provider>
  );
};

export const usePCConfiguration = () => {
  const context = useContext(PCConfigContext);
  if (context === undefined) {
    throw new Error("usePCConfiguration must be used within a PCConfigProvider");
  }
  return context;
};
